import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import TopBar from '../components/layout/TopBar';
import GlassCard from '../components/ui/GlassCard';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import StatCard from '../components/ui/StatCard';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import InputField from '../components/ui/InputField';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { useNavigate } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────
interface PaymentSchedule {
  id: string;
  saleId: string;
  amount: number;
  amountPaid: number;
  dueDate: string;
  paidDate: string | null;
  status: string;
  receiptNumber: string | null;
  paymentMethod: string | null;
  referenceNumber: string | null;
  penaltyAmount: number;
  penaltyApplied: boolean;
  installmentNo: number;
  notes: string | null;
  sale?: {
    id: string;
    saleAmount: number;
    client: { id: string; firstName: string; lastName: string; phone: string; email: string | null };
    lot: { id: string; lotNumber: string; sectionId: string };
    agent: { id: string; name: string };
  };
}

interface LedgerEntry {
  id: string;
  clientId: string;
  saleId: string | null;
  paymentId: string | null;
  type: string;
  amount: number;
  runningBalance: number;
  description: string;
  referenceNumber: string | null;
  performedBy: string | null;
  createdAt: string;
  client?: { id: string; firstName: string; lastName: string };
  sale?: { id: string; lotId: string; saleAmount: number } | null;
  payment?: { id: string; installmentNo: number; dueDate: string } | null;
}

interface PaymentStats {
  totalScheduledAmount: number;
  totalCollected: number;
  collectionEfficiency: number;
  overdueCount: number;
  pendingCount: number;
  upcomingDue7Days: number;
  monthlyCollected: number;
  totalPenalties: number;
}

interface OverdueSummary {
  totalOverdue: number;
  totalOverdueAmount: number;
  aging: { current: number; days30: number; days60: number; days90: number; days90plus: number };
  agingAmounts: { current: number; days30: number; days60: number; days90: number; days90plus: number };
  payments: PaymentSchedule[];
}

// ─── Helpers ──────────────────────────────────────────────────
const fmt = (n: number) => '₱' + n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });

const statusVariant = (status: string): 'available' | 'reserved' | 'sold' | 'info' | 'default' => {
  switch (status) {
    case 'PAID': return 'available';
    case 'PARTIAL': return 'reserved';
    case 'OVERDUE': return 'sold';
    case 'PENDING': return 'default';
    case 'CANCELLED': return 'info';
    default: return 'default';
  }
};

const ledgerTypeConfig: Record<string, { label: string; icon: string; color: string }> = {
  PAYMENT: { label: 'Payment', icon: 'solar:wallet-money-bold-duotone', color: '#10B981' },
  PENALTY: { label: 'Penalty', icon: 'solar:danger-triangle-bold-duotone', color: '#EF4444' },
  ADJUSTMENT: { label: 'Adjustment', icon: 'solar:pen-new-square-bold-duotone', color: '#F59E0B' },
  REFUND: { label: 'Refund', icon: 'solar:undo-left-bold-duotone', color: '#8B5CF6' },
  DOWN_PAYMENT: { label: 'Down Payment', icon: 'solar:hand-money-bold-duotone', color: '#0D9488' },
  DISCOUNT: { label: 'Discount', icon: 'solar:tag-price-bold-duotone', color: '#06B6D4' },
};

const PAYMENT_METHODS = ['CASH', 'CHECK', 'BANK_TRANSFER', 'GCASH', 'CARD'];

// ─── Component ────────────────────────────────────────────────
const Payments: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();

  // State
  const [viewMode, setViewMode] = useState<'schedules' | 'ledger' | 'overdue' | 'actions'>('schedules');
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [schedules, setSchedules] = useState<PaymentSchedule[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [overdueSummary, setOverdueSummary] = useState<OverdueSummary | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Record Payment Modal
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordForm, setRecordForm] = useState({ saleId: '', amount: '', paymentMethod: 'CASH', referenceNumber: '', receiptNumber: '', notes: '' });
  const [saleSearch, setSaleSearch] = useState('');
  const [salesList, setSalesList] = useState<any[]>([]);
  const [selectedSale, setSelectedSale] = useState<any>(null);
  const [showSaleDropdown, setShowSaleDropdown] = useState(false);

  // Adjustment Modal
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ clientId: '', saleId: '', type: 'ADJUSTMENT', amount: '', description: '', referenceNumber: '' });

  // Confirmation Modals
  const [showPayConfirm, setShowPayConfirm] = useState(false);
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<PaymentSchedule | null>(null);

  // Action loading states
  const [detectingOverdue, setDetectingOverdue] = useState(false);
  const [applyingPenalties, setApplyingPenalties] = useState(false);

  // ─── Data Fetching ──────────────────────────────────────────
  const fetchSales = useCallback(async () => {
    try {
      const data = await api.sales.list();
      setSalesList(data.sales || []);
    } catch (e) { console.error('Failed to fetch sales:', e); }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await api.payments.stats();
      setStats(data);
    } catch (e) { console.error('Failed to fetch payment stats:', e); }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (filterStatus) params.status = filterStatus;
      const data = await api.payments.schedules.list(params);
      setSchedules(data.payments || []);
    } catch (e) { console.error('Failed to fetch schedules:', e); }
  }, [filterStatus]);

  const fetchLedger = useCallback(async () => {
    try {
      const data = await api.payments.ledger.list();
      setLedgerEntries(data.entries || []);
    } catch (e) { console.error('Failed to fetch ledger:', e); }
  }, []);

  const fetchOverdue = useCallback(async () => {
    try {
      const data = await api.payments.overdue.summary();
      setOverdueSummary(data);
    } catch (e) { console.error('Failed to fetch overdue summary:', e); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchSchedules(), fetchLedger(), fetchOverdue(), fetchSales()]);
      setLoading(false);
    };
    init();
  }, [fetchStats, fetchSchedules, fetchLedger, fetchOverdue, fetchSales]);

  // ─── Actions ────────────────────────────────────────────────
  const handleRecordPayment = async () => {
    try {
      if (!recordForm.saleId || !recordForm.amount) {
        addToast('error', 'Sale ID and amount are required');
        return;
      }
      const result = await api.payments.record({
        saleId: recordForm.saleId,
        amount: Number(recordForm.amount),
        paymentMethod: recordForm.paymentMethod || undefined,
        referenceNumber: recordForm.referenceNumber || undefined,
        receiptNumber: recordForm.receiptNumber || undefined,
        notes: recordForm.notes || undefined,
      });
      addToast('success', `Payment recorded — ${result.allocations.length} installment(s) allocated, balance: ${fmt(result.saleBalance)}`);
      setShowRecordModal(false);
      setRecordForm({ saleId: '', amount: '', paymentMethod: 'CASH', referenceNumber: '', receiptNumber: '', notes: '' });
      setSelectedSale(null);
      setSaleSearch('');
      fetchStats(); fetchSchedules(); fetchLedger();
    } catch (e: any) {
      addToast('error', e.message || 'Failed to record payment');
    }
  };

  const handleDetectOverdue = async () => {
    setDetectingOverdue(true);
    try {
      const result = await api.payments.overdue.detect();
      addToast('success', `Detected ${result.detected} overdue payment(s), updated ${result.updated}`);
      fetchStats(); fetchSchedules(); fetchOverdue();
    } catch (e: any) {
      console.error('Detect overdue error:', e);
      addToast('error', e.message || 'Failed to detect overdue');
    } finally {
      setDetectingOverdue(false);
    }
  };

  const handleComputePenalties = async () => {
    setApplyingPenalties(true);
    try {
      const result = await api.payments.penalties.compute({ penaltyRate: 0.02, minDaysOverdue: 1 });
      addToast('success', `Applied penalties to ${result.penalized} payment(s)`);
      fetchStats(); fetchSchedules(); fetchLedger(); fetchOverdue();
    } catch (e: any) {
      console.error('Compute penalties error:', e);
      addToast('error', e.message || 'Failed to compute penalties');
    } finally {
      setApplyingPenalties(false);
    }
  };

  const handleCreateAdjustment = async () => {
    try {
      if (!adjustForm.clientId || !adjustForm.amount || !adjustForm.description) {
        addToast('error', 'Client ID, amount, and description are required');
        return;
      }
      await api.payments.ledger.create({
        clientId: adjustForm.clientId,
        saleId: adjustForm.saleId || undefined,
        type: adjustForm.type,
        amount: Number(adjustForm.amount),
        description: adjustForm.description,
        referenceNumber: adjustForm.referenceNumber || undefined,
      });
      addToast('success', 'Ledger adjustment created');
      setShowAdjustmentModal(false);
      setAdjustForm({ clientId: '', saleId: '', type: 'ADJUSTMENT', amount: '', description: '', referenceNumber: '' });
      fetchLedger(); fetchStats();
    } catch (e: any) {
      addToast('error', e.message || 'Failed to create adjustment');
    }
  };

  // ─── Filtered Data ──────────────────────────────────────────
  const filteredSchedules = schedules.filter(s => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const clientName = s.sale ? `${s.sale.client.firstName} ${s.sale.client.lastName}`.toLowerCase() : '';
      const lotNum = s.sale?.lot?.lotNumber?.toLowerCase() || '';
      if (!clientName.includes(q) && !lotNum.includes(q) && !s.saleId.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // ─── View Tabs ──────────────────────────────────────────────
  const viewTabs = [
    { key: 'schedules' as const, label: 'Schedules', icon: 'solar:calendar-bold-duotone' },
    { key: 'ledger' as const, label: 'Ledger', icon: 'solar:notebook-bold-duotone' },
    { key: 'overdue' as const, label: 'Overdue', icon: 'solar:danger-triangle-bold-duotone' },
    { key: 'actions' as const, label: 'Actions', icon: 'solar:bolt-circle-bold-duotone' },
  ];

  // ─── Quick Action Handlers ──────────────────────────────────
  const handleMarkAsPaidClick = (payment: PaymentSchedule) => {
    setPendingAction(payment);
    setShowPayConfirm(true);
  };

  const confirmMarkAsPaid = async () => {
    if (!pendingAction) return;
    try {
      await api.sales.recordPayment(pendingAction.id, { amountPaid: pendingAction.amount });
      addToast('success', `Payment marked as paid - ${fmt(pendingAction.amount)}`);
      fetchSchedules();
      fetchStats();
      fetchLedger();
      setShowPayConfirm(false);
      setPendingAction(null);
    } catch (e: any) {
      addToast('error', e.message || 'Failed to mark as paid');
    }
  };

  const handleViewSale = (saleId: string) => {
    // Navigate to Sales page with the sale ID
    navigate('/sales', { state: { saleId } });
  };

  const handleSendReminderClick = (payment: PaymentSchedule) => {
    setPendingAction(payment);
    setShowReminderConfirm(true);
  };

  const confirmSendReminder = async () => {
    if (!pendingAction) return;
    try {
      // API call to send reminder (if available)
      addToast('success', `Reminder sent to ${pendingAction.sale?.client.firstName} ${pendingAction.sale?.client.lastName}`);
      setShowReminderConfirm(false);
      setPendingAction(null);
    } catch (e: any) {
      addToast('error', e.message || 'Failed to send reminder');
    }
  };

  // ─── Schedule Table Columns ─────────────────────────────────
  const scheduleColumns = [
    {
      key: 'installmentNo', header: '#', width: '50px',
      render: (p: PaymentSchedule) => (
        <span style={{ color: 'var(--text-secondary)', fontWeight: 600, fontSize: '12px' }}>{p.installmentNo || '—'}</span>
      ),
    },
    {
      key: 'client', header: 'Client',
      render: (p: PaymentSchedule) => (
        <div>
          <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>
            {p.sale ? `${p.sale.client.firstName} ${p.sale.client.lastName}` : '—'}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
            Lot {p.sale?.lot?.lotNumber || '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'dueDate', header: 'Due Date',
      render: (p: PaymentSchedule) => (
        <span style={{ color: 'var(--text-primary)', fontSize: '12px' }}>{fmtDate(p.dueDate)}</span>
      ),
    },
    {
      key: 'amount', header: 'Amount',
      render: (p: PaymentSchedule) => (
        <div>
          <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{fmt(p.amount)}</div>
          {p.penaltyAmount > 0 && (
            <div style={{ color: '#EF4444', fontSize: '10px' }}>+{fmt(p.penaltyAmount)} penalty</div>
          )}
        </div>
      ),
    },
    {
      key: 'paid', header: 'Paid',
      render: (p: PaymentSchedule) => (
        <span style={{ color: p.amountPaid > 0 ? '#10B981' : 'var(--text-muted)', fontWeight: 600, fontSize: '13px' }}>
          {p.amountPaid > 0 ? fmt(p.amountPaid) : '—'}
        </span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (p: PaymentSchedule) => <Badge variant={statusVariant(p.status)}>{p.status}</Badge>,
    },
    {
      key: 'method', header: 'Method',
      render: (p: PaymentSchedule) => (
        <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>{p.paymentMethod || '—'}</span>
      ),
    },
    {
      key: 'actions', header: 'Actions', width: '140px',
      render: (p: PaymentSchedule) => (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {p.status === 'PENDING' && (
            <button
              onClick={() => handleMarkAsPaidClick(p)}
              title="Mark as Paid"
              style={{
                background: 'rgba(16,185,129,0.10)', border: 'none', borderRadius: '6px',
                padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(16,185,129,0.20)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(16,185,129,0.10)'}
            >
              <Icon icon="solar:check-circle-bold-duotone" width={16} style={{ color: '#10B981' }} />
              <span style={{ fontSize: '11px', fontWeight: 600, color: '#10B981' }}>Pay</span>
            </button>
          )}
          {(p.status === 'OVERDUE' || p.status === 'PENDING') && (
            <button
              onClick={() => handleSendReminderClick(p)}
              title="Send Reminder"
              style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border-base)', borderRadius: '6px',
                padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--nav-item-active-bg)';
                e.currentTarget.style.borderColor = 'var(--primary-400)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--bg-elevated)';
                e.currentTarget.style.borderColor = 'var(--border-base)';
              }}
            >
              <Icon icon="solar:bell-bing-bold-duotone" width={16} style={{ color: 'var(--text-secondary)' }} />
            </button>
          )}
          <button
            onClick={() => handleViewSale(p.saleId)}
            title="View Sale Details"
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border-base)', borderRadius: '6px',
              padding: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--nav-item-active-bg)';
              e.currentTarget.style.borderColor = 'var(--primary-400)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--bg-elevated)';
              e.currentTarget.style.borderColor = 'var(--border-base)';
            }}
          >
            <Icon icon="solar:eye-bold-duotone" width={16} style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>
      ),
    },
  ];

  // ─── Ledger Table Columns ───────────────────────────────────
  const ledgerColumns = [
    {
      key: 'date', header: 'Date', width: '120px',
      render: (e: LedgerEntry) => (
        <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{fmtDate(e.createdAt)}</span>
      ),
    },
    {
      key: 'type', header: 'Type', width: '130px',
      render: (e: LedgerEntry) => {
        const cfg = ledgerTypeConfig[e.type] || { label: e.type, icon: 'solar:document-bold-duotone', color: 'var(--text-secondary)' };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Icon icon={cfg.icon} width={14} style={{ color: cfg.color }} />
            <span style={{ color: cfg.color, fontSize: '12px', fontWeight: 600 }}>{cfg.label}</span>
          </div>
        );
      },
    },
    {
      key: 'client', header: 'Client',
      render: (e: LedgerEntry) => (
        <span style={{ color: 'var(--text-primary)', fontSize: '12px' }}>
          {e.client ? `${e.client.firstName} ${e.client.lastName}` : '—'}
        </span>
      ),
    },
    {
      key: 'description', header: 'Description',
      render: (e: LedgerEntry) => (
        <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{e.description}</span>
      ),
    },
    {
      key: 'amount', header: 'Amount', width: '120px',
      render: (e: LedgerEntry) => (
        <span style={{ color: e.amount >= 0 ? '#10B981' : '#EF4444', fontWeight: 700, fontSize: '13px' }}>
          {e.amount >= 0 ? '+' : ''}{fmt(e.amount)}
        </span>
      ),
    },
    {
      key: 'balance', header: 'Balance', width: '120px',
      render: (e: LedgerEntry) => (
        <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{fmt(e.runningBalance)}</span>
      ),
    },
  ];

  // ─── RENDER: Stats KPIs ─────────────────────────────────────
  const renderStats = () => {
    if (!stats) return null;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <StatCard label="Collection Efficiency" value={`${stats.collectionEfficiency}%`}
          icon="solar:chart-bold-duotone" iconColor="#0D9488" iconBg="rgba(13,148,136,0.12)" />
        <StatCard label="Total Collected" value={fmt(stats.totalCollected)}
          icon="solar:wallet-money-bold-duotone" iconColor="#10B981" iconBg="rgba(16,185,129,0.12)" />
        <StatCard label="Monthly Collected" value={fmt(stats.monthlyCollected)}
          icon="solar:calendar-bold-duotone" iconColor="#06B6D4" iconBg="rgba(6,182,212,0.12)" />
        <StatCard label="Overdue" value={String(stats.overdueCount)}
          icon="solar:danger-triangle-bold-duotone" iconColor="#EF4444" iconBg="rgba(239,68,68,0.12)" />
        <StatCard label="Pending" value={String(stats.pendingCount)}
          icon="solar:clock-circle-bold-duotone" iconColor="#F59E0B" iconBg="rgba(245,158,11,0.12)" />
        <StatCard label="Due in 7 Days" value={String(stats.upcomingDue7Days)}
          icon="solar:alarm-bold-duotone" iconColor="#8B5CF6" iconBg="rgba(139,92,246,0.12)" />
        <StatCard label="Total Scheduled" value={fmt(stats.totalScheduledAmount)}
          icon="solar:document-bold-duotone" iconColor="#64748B" iconBg="rgba(100,116,139,0.12)" />
        <StatCard label="Total Penalties" value={fmt(stats.totalPenalties)}
          icon="solar:shield-warning-bold-duotone" iconColor="#DC2626" iconBg="rgba(220,38,38,0.12)" />
      </div>
    );
  };

  // ─── RENDER: Schedules View ─────────────────────────────────
  const renderSchedules = () => (
    <DataTable columns={scheduleColumns} data={filteredSchedules} />
  );

  // ─── RENDER: Ledger View ────────────────────────────────────
  const renderLedger = () => (
    <DataTable columns={ledgerColumns} data={ledgerEntries} />
  );

  // ─── RENDER: Overdue View ───────────────────────────────────
  const renderOverdue = () => {
    if (!overdueSummary) return <div style={{ color: 'var(--text-muted)', padding: '40px', textAlign: 'center' }}>Loading overdue data...</div>;

    const { aging, agingAmounts } = overdueSummary;
    const agingBuckets = [
      { label: 'Current', count: aging.current, amount: agingAmounts.current, color: '#10B981' },
      { label: '1–30 Days', count: aging.days30, amount: agingAmounts.days30, color: '#F59E0B' },
      { label: '31–60 Days', count: aging.days60, amount: agingAmounts.days60, color: '#F97316' },
      { label: '61–90 Days', count: aging.days90, amount: agingAmounts.days90, color: '#EF4444' },
      { label: '90+ Days', count: aging.days90plus, amount: agingAmounts.days90plus, color: '#DC2626' },
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Summary banner */}
        <GlassCard style={{ borderLeft: '3px solid #EF4444' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon icon="solar:danger-triangle-bold-duotone" width={24} style={{ color: '#EF4444' }} />
            <div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '16px' }}>
                {overdueSummary.totalOverdue} Overdue Payments
              </div>
              <div style={{ color: '#EF4444', fontWeight: 600, fontSize: '14px' }}>
                Total Outstanding: {fmt(overdueSummary.totalOverdueAmount)}
              </div>
            </div>
            <div style={{ flex: 1 }} />
            <Button variant="secondary" size="sm" icon="solar:refresh-bold-duotone" onClick={handleDetectOverdue} disabled={detectingOverdue}>
              {detectingOverdue ? 'Detecting...' : 'Detect Overdue'}
            </Button>
            <Button variant="secondary" size="sm" icon="solar:shield-warning-bold-duotone" onClick={handleComputePenalties}
              style={{ color: '#EF4444' }} disabled={applyingPenalties}>
              {applyingPenalties ? 'Applying...' : 'Apply Penalties'}
            </Button>
          </div>
        </GlassCard>

        {/* Aging buckets */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
          {agingBuckets.map(b => (
            <GlassCard key={b.label} style={{ borderTop: `3px solid ${b.color}`, textAlign: 'center', padding: '14px' }}>
              <div style={{ color: b.color, fontWeight: 800, fontSize: '22px' }}>{b.count}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>{b.label}</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{fmt(b.amount)}</div>
            </GlassCard>
          ))}
        </div>

        {/* Overdue payment list */}
        {overdueSummary.payments && overdueSummary.payments.length > 0 && (
          <DataTable columns={scheduleColumns} data={overdueSummary.payments} />
        )}
      </div>
    );
  };

  // ─── RENDER: Actions View ───────────────────────────────────
  const renderActions = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
      {/* Record Payment */}
      <GlassCard style={{ borderLeft: '3px solid #10B981' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(16,185,129,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon icon="solar:wallet-money-bold-duotone" width={20} style={{ color: '#10B981' }} />
          </div>
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '15px' }}>Record Payment</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>FIFO allocation to oldest unpaid installments</div>
          </div>
        </div>
        <Button variant="primary" size="sm" icon="solar:add-circle-bold-duotone" onClick={() => setShowRecordModal(true)}>
          Record New Payment
        </Button>
      </GlassCard>

      {/* Detect Overdue */}
      <GlassCard style={{ borderLeft: '3px solid #F59E0B' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(245,158,11,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon icon="solar:clock-circle-bold-duotone" width={20} style={{ color: '#F59E0B' }} />
          </div>
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '15px' }}>Detect Overdue</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Scan and mark all past-due pending payments</div>
          </div>
        </div>
        <Button variant="secondary" size="sm" icon="solar:refresh-bold-duotone" onClick={handleDetectOverdue}>
          Run Detection
        </Button>
      </GlassCard>

      {/* Compute Penalties */}
      <GlassCard style={{ borderLeft: '3px solid #EF4444' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(239,68,68,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon icon="solar:shield-warning-bold-duotone" width={20} style={{ color: '#EF4444' }} />
          </div>
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '15px' }}>Compute Penalties</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Apply 2% penalty on overdue payments</div>
          </div>
        </div>
        <Button variant="secondary" size="sm" icon="solar:danger-triangle-bold-duotone" onClick={handleComputePenalties}
          style={{ color: '#EF4444' }}>
          Apply Penalties
        </Button>
      </GlassCard>

      {/* Manual Adjustment */}
      <GlassCard style={{ borderLeft: '3px solid #8B5CF6' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(139,92,246,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon icon="solar:pen-new-square-bold-duotone" width={20} style={{ color: '#8B5CF6' }} />
          </div>
          <div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '15px' }}>Manual Adjustment</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Create ledger adjustments, refunds, or discounts</div>
          </div>
        </div>
        <Button variant="secondary" size="sm" icon="solar:add-circle-bold-duotone" onClick={() => setShowAdjustmentModal(true)}>
          Create Adjustment
        </Button>
      </GlassCard>
    </div>
  );

  // ─── Main Render ────────────────────────────────────────────
  return (
    <>
      <TopBar title="Payments & Ledger" />
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, minHeight: 0 }}>
        {/* KPI Stats */}
        {renderStats()}

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* View Tabs */}
          <div style={{
            display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden',
            border: '1px solid var(--glass-border)',
          }}>
            {viewTabs.map(tab => (
              <button key={tab.key} onClick={() => setViewMode(tab.key)}
                style={{
                  padding: '7px 14px', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: viewMode === tab.key ? 'var(--nav-item-active-bg)' : 'var(--bg-elevated)',
                  color: viewMode === tab.key ? 'var(--nav-item-active-color)' : 'var(--text-muted)',
                  fontFamily: 'var(--font-body)', transition: 'all 0.15s ease',
                  display: 'flex', alignItems: 'center', gap: '5px',
                }}>
                <Icon icon={tab.icon} width={14} />
                {tab.label}
                {tab.key === 'overdue' && stats && stats.overdueCount > 0 && (
                  <span style={{
                    background: '#EF4444', color: '#fff', borderRadius: '8px',
                    fontSize: '10px', fontWeight: 700, padding: '1px 6px', marginLeft: '2px',
                  }}>{stats.overdueCount}</span>
                )}
              </button>
            ))}
          </div>

          {/* Search (schedules) */}
          {viewMode === 'schedules' && (
            <>
              <div style={{ width: '200px' }}>
                <InputField placeholder="Search client/lot..." icon="solar:magnifer-bold-duotone"
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
              </div>
              <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); }}
                style={{
                  background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)', padding: '8px 12px', color: 'var(--input-text)',
                  fontFamily: 'var(--font-body)', fontSize: '12px', outline: 'none', cursor: 'pointer',
                }}>
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="PAID">Paid</option>
                <option value="PARTIAL">Partial</option>
                <option value="OVERDUE">Overdue</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </>
          )}

          <div style={{ flex: 1 }} />

          {/* Quick record */}
          <Button variant="primary" size="sm" icon="solar:wallet-money-bold-duotone" onClick={() => setShowRecordModal(true)}>
            Record Payment
          </Button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: 'var(--text-muted)' }}>
              <Icon icon="solar:refresh-bold-duotone" width={20} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
              Loading payments...
            </div>
          ) : (
            <>
              {viewMode === 'schedules' && renderSchedules()}
              {viewMode === 'ledger' && renderLedger()}
              {viewMode === 'overdue' && renderOverdue()}
              {viewMode === 'actions' && renderActions()}
            </>
          )}
        </div>
      </div>

      {/* ─── Record Payment Modal ──────────────────────────────── */}
      {showRecordModal && (
        <Modal isOpen={showRecordModal} title="Record Payment (FIFO)" onClose={() => { setShowRecordModal(false); setSelectedSale(null); setSaleSearch(''); }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
            {/* Sale Search */}
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Search Sale</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Search by client name, lot number, or sale ID..."
                  value={saleSearch}
                  onChange={e => { setSaleSearch(e.target.value); setShowSaleDropdown(true); }}
                  onFocus={() => setShowSaleDropdown(true)}
                  style={{
                    width: '100%', background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                    borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--input-text)',
                    fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none',
                  }}
                />
                {showSaleDropdown && saleSearch && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                    background: 'var(--modal-bg)', border: '1px solid var(--border-base)',
                    borderRadius: '0 0 var(--radius-md) var(--radius-md)', maxHeight: '200px', overflowY: 'auto',
                    marginTop: '2px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}>
                    {salesList
                      .filter(s => {
                        const search = saleSearch.toLowerCase();
                        const clientName = `${s.client?.firstName || ''} ${s.client?.lastName || ''}`.toLowerCase();
                        const lotNumber = s.lot?.lotNumber?.toLowerCase() || '';
                        const saleId = s.id.toLowerCase();
                        return clientName.includes(search) || lotNumber.includes(search) || saleId.includes(search);
                      })
                      .slice(0, 10)
                      .map(sale => (
                        <button
                          key={sale.id}
                          onClick={() => {
                            setSelectedSale(sale);
                            setRecordForm(f => ({ ...f, saleId: sale.id, amount: String(sale.balance || 0) }));
                            setSaleSearch(`${sale.client?.firstName} ${sale.client?.lastName} - Lot ${sale.lot?.lotNumber}`);
                            setShowSaleDropdown(false);
                          }}
                          style={{
                            width: '100%', textAlign: 'left', padding: '10px 12px',
                            background: 'none', border: 'none', borderBottom: '1px solid var(--border-base)',
                            cursor: 'pointer', fontFamily: 'var(--font-body)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                                <div style={{ 
                                  background: 'var(--primary-600)', color: '#fff', 
                                  padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700,
                                }}>
                                  {sale.lot?.lotNumber}
                                </div>
                                <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>
                                  {sale.client?.firstName} {sale.client?.lastName}
                                </div>
                              </div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
                                {fmt(sale.saleAmount)} · ID: {sale.id.slice(0, 8)}...
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ color: sale.balance > 0 ? '#EF4444' : '#10B981', fontSize: '12px', fontWeight: 700 }}>
                                {fmt(sale.balance)}
                              </div>
                              <div style={{ color: 'var(--text-faint)', fontSize: '10px' }}>balance</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    {salesList.filter(s => {
                      const search = saleSearch.toLowerCase();
                      const clientName = `${s.client?.firstName || ''} ${s.client?.lastName || ''}`.toLowerCase();
                      const lotNumber = s.lot?.lotNumber?.toLowerCase() || '';
                      const saleId = s.id.toLowerCase();
                      return clientName.includes(search) || lotNumber.includes(search) || saleId.includes(search);
                    }).length === 0 && (
                      <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
                        No sales found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Selected Sale Info */}
            {selectedSale && (
              <div style={{
                padding: '12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)',
                border: '1px solid var(--border-base)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>
                      {selectedSale.client?.firstName} {selectedSale.client?.lastName}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                      Lot {selectedSale.lot?.lotNumber} · Agent: {selectedSale.agent?.name}
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedSale(null); setSaleSearch(''); setRecordForm(f => ({ ...f, saleId: '', amount: '' })); }}
                    style={{
                      background: 'none', border: 'none', color: 'var(--text-muted)',
                      cursor: 'pointer', padding: '4px',
                    }}
                  >
                    <Icon icon="solar:close-circle-bold-duotone" width={18} />
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '11px' }}>
                  <div>
                    <div style={{ color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '9px' }}>Sale Amount</div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{fmt(selectedSale.saleAmount)}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '9px' }}>Paid</div>
                    <div style={{ color: '#10B981', fontWeight: 600 }}>{fmt(selectedSale.totalPaid || 0)}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '9px' }}>Balance</div>
                    <div style={{ color: selectedSale.balance > 0 ? '#EF4444' : '#10B981', fontWeight: 700 }}>{fmt(selectedSale.balance)}</div>
                  </div>
                </div>
                {selectedSale.balance > 0 && (
                  <button
                    onClick={() => setRecordForm(f => ({ ...f, amount: String(selectedSale.balance) }))}
                    style={{
                      marginTop: '8px', width: '100%', padding: '6px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--primary-600)', border: 'none', color: '#fff',
                      fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
                    }}
                  >
                    Pay Full Balance ({fmt(selectedSale.balance)})
                  </button>
                )}
              </div>
            )}

            <InputField label="Amount (₱)" placeholder="0.00" type="number" value={recordForm.amount}
              onChange={e => setRecordForm(f => ({ ...f, amount: e.target.value }))} />
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Payment Method</label>
              <select value={recordForm.paymentMethod}
                onChange={e => setRecordForm(f => ({ ...f, paymentMethod: e.target.value }))}
                style={{
                  width: '100%', background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--input-text)',
                  fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none',
                }}>
                {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
              </select>
            </div>
            <InputField label="Receipt #" placeholder="OR-000001" value={recordForm.receiptNumber}
              onChange={e => setRecordForm(f => ({ ...f, receiptNumber: e.target.value }))} />
            <InputField label="Reference #" placeholder="Optional" value={recordForm.referenceNumber}
              onChange={e => setRecordForm(f => ({ ...f, referenceNumber: e.target.value }))} />
            <InputField label="Notes" placeholder="Optional" value={recordForm.notes}
              onChange={e => setRecordForm(f => ({ ...f, notes: e.target.value }))} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <Button variant="ghost" onClick={() => setShowRecordModal(false)}>Cancel</Button>
              <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleRecordPayment}>
                Record & Allocate
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ─── Adjustment Modal ──────────────────────────────────── */}
      {showAdjustmentModal && (
        <Modal isOpen={showAdjustmentModal} title="Create Ledger Adjustment" onClose={() => setShowAdjustmentModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
            <InputField label="Client ID" placeholder="Paste client ID" value={adjustForm.clientId}
              onChange={e => setAdjustForm(f => ({ ...f, clientId: e.target.value }))} />
            <InputField label="Sale ID (optional)" placeholder="Paste sale ID" value={adjustForm.saleId}
              onChange={e => setAdjustForm(f => ({ ...f, saleId: e.target.value }))} />
            <div>
              <label style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Type</label>
              <select value={adjustForm.type}
                onChange={e => setAdjustForm(f => ({ ...f, type: e.target.value }))}
                style={{
                  width: '100%', background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--input-text)',
                  fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none',
                }}>
                <option value="ADJUSTMENT">Adjustment</option>
                <option value="REFUND">Refund</option>
                <option value="DISCOUNT">Discount</option>
                <option value="PENALTY">Penalty</option>
              </select>
            </div>
            <InputField label="Amount (₱)" placeholder="Positive = credit, negative = debit" type="number" value={adjustForm.amount}
              onChange={e => setAdjustForm(f => ({ ...f, amount: e.target.value }))} />
            <InputField label="Description" placeholder="Reason for adjustment" value={adjustForm.description}
              onChange={e => setAdjustForm(f => ({ ...f, description: e.target.value }))} />
            <InputField label="Reference #" placeholder="Optional" value={adjustForm.referenceNumber}
              onChange={e => setAdjustForm(f => ({ ...f, referenceNumber: e.target.value }))} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <Button variant="ghost" onClick={() => setShowAdjustmentModal(false)}>Cancel</Button>
              <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleCreateAdjustment}>
                Create Entry
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ─── Mark as Paid Confirmation ─────────────────────────── */}
      {showPayConfirm && pendingAction && (
        <Modal isOpen={showPayConfirm} title="Confirm Payment" onClose={() => { setShowPayConfirm(false); setPendingAction(null); }}>
          <div style={{ padding: '8px 0' }}>
            <div style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '16px',
              border: '1px solid var(--border-base)',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Icon icon="solar:check-circle-bold-duotone" width={32} style={{ color: '#10B981' }} />
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px' }}>
                    Mark Payment as Paid
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    This action will record the full payment amount
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Client</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {pendingAction.sale?.client.firstName} {pendingAction.sale?.client.lastName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lot</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {pendingAction.sale?.lot.lotNumber}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Amount</div>
                  <div style={{ color: '#10B981', fontWeight: 700, fontSize: '16px' }}>
                    {fmt(pendingAction.amount)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Due Date</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {fmtDate(pendingAction.dueDate)}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button variant="ghost" onClick={() => { setShowPayConfirm(false); setPendingAction(null); }}>
                Cancel
              </Button>
              <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={confirmMarkAsPaid}>
                Confirm Payment
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ─── Send Reminder Confirmation ────────────────────────── */}
      {showReminderConfirm && pendingAction && (
        <Modal isOpen={showReminderConfirm} title="Send Payment Reminder" onClose={() => { setShowReminderConfirm(false); setPendingAction(null); }}>
          <div style={{ padding: '8px 0' }}>
            <div style={{ 
              background: 'var(--bg-elevated)', 
              borderRadius: 'var(--radius-md)', 
              padding: '16px',
              border: '1px solid var(--border-base)',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Icon icon="solar:bell-bing-bold-duotone" width={32} style={{ color: '#F59E0B' }} />
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px' }}>
                    Send Payment Reminder
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    A reminder notification will be sent to the client
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Client</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {pendingAction.sale?.client.firstName} {pendingAction.sale?.client.lastName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Phone</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {pendingAction.sale?.client.phone}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Amount Due</div>
                  <div style={{ color: pendingAction.status === 'OVERDUE' ? '#EF4444' : '#F59E0B', fontWeight: 700, fontSize: '16px' }}>
                    {fmt(pendingAction.amount - pendingAction.amountPaid)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Due Date</div>
                  <div style={{ color: pendingAction.status === 'OVERDUE' ? '#EF4444' : 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                    {fmtDate(pendingAction.dueDate)}
                    {pendingAction.status === 'OVERDUE' && <span style={{ marginLeft: '6px', fontSize: '11px' }}>(Overdue)</span>}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button variant="ghost" onClick={() => { setShowReminderConfirm(false); setPendingAction(null); }}>
                Cancel
              </Button>
              <Button variant="primary" icon="solar:bell-bing-bold-duotone" onClick={confirmSendReminder}>
                Send Reminder
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Payments;
